import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';

import cn from 'classnames';
import Dialog from 'components/Dialog';
import { Button, LoaderSpinner, PercentInput, TextField } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';
import { exportActivityDetails, useActivityDetailsAPI } from 'services/course-api';
import { pluralize, toFixedIfFloat } from 'utils';

// time in seconds
const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

const getLateBy = (seconds: number) => {
  if (seconds === 0) return 'On Time';

  if (seconds >= DAY) {
    const days = Math.round(seconds / DAY);
    return `+ ${days} ${pluralize(days, 'day')}`;
  }

  if (seconds >= HOUR) {
    const hours = Math.round(seconds / HOUR);
    return `+ ${hours} ${pluralize(hours, 'hour')}`;
  }

  if (seconds >= MINUTE) {
    const minutes = Math.round(seconds / MINUTE);
    return `+ ${minutes} ${pluralize(minutes, 'minute')}`;
  }

  return `+ ${seconds} ${pluralize(seconds, 'second')}`;
};

const getStylesForLateBy = (seconds: number) => {
  if (seconds === 0) return 'good';
  if (seconds <= 15 * MINUTE) return 'ok';
  else return 'bad';
};

const getStylesForScore = (score: number) => {
  if (score >= 80) return 'good';
  if (score >= 60) return 'ok';
  else return 'bad';
};

const getStylesForPenalty = (score: number) => {
  if (score === 0) return 'good';
  if (score <= 20) return 'ok';
  else return 'bad';
};

interface AssignmentParticipationsProps {
  isOpen: boolean;
  activityId: string;
  onClose(): void;
  onExport(): void;
}

interface Penalty {
  lateBy: number;
  amount: number | undefined;
}

interface UpdatePenaltyAction {
  type: 'UPDATE_PENALTY';
  userId: string;
  amount: number | undefined;
}

interface SetPenaltyAction {
  type: 'SET_PENALTY';
  userId: string;
  penalty: Penalty;
}

const individualPenaltyReducer = (
  state: HashMap<Penalty>,
  action: SetPenaltyAction | UpdatePenaltyAction
) => {
  switch (action.type) {
    case 'SET_PENALTY':
      return {
        ...state,
        [action.userId]: action.penalty,
      };
    case 'UPDATE_PENALTY':
      return {
        ...state,
        [action.userId]: {
          ...state[action.userId],
          amount: action.amount,
        },
      };
    default:
      throw new Error('Invalid action');
  }
};

const AssignmentParticipations: FC<AssignmentParticipationsProps> = ({
  isOpen,
  activityId,
  onClose,
  onExport,
}) => {
  const [latePenalty, setLatePenalty] = useState<number | undefined>(0);
  const [maxScore, setMaxScore] = useState<number | undefined>(0);
  const [isExporting, setIsExporting] = useState(false);

  const [individualPenaltyMap, setIndividualPenaltyMap] = useReducer(
    individualPenaltyReducer,
    {} as HashMap<Penalty>
  );

  const { data: activity, isLoading } = useActivityDetailsAPI<AssignmentDetails>(activityId, 'assignment');

  useEffect(() => {
    if (!activity) return;
    setMaxScore(activity.details.maxScore);
    setLatePenalty(activity.details.latePenalty);
  }, [activity]);

  useEffect(() => {
    activity?.details.students.forEach((s) => {
      setIndividualPenaltyMap({
        type: 'SET_PENALTY',
        userId: s.userId,
        penalty: {
          lateBy: s.lateBy,
          amount: s.lateBy === 0 ? 0 : latePenalty,
        },
      });
    });
  }, [activity, latePenalty]);

  const exportActivityData = useCallback(async () => {
    setIsExporting(true);

    const particulars: { userId: string; latePenalty: number }[] = [];

    for (const [userId, penalty] of Object.entries(individualPenaltyMap)) {
      const isLate = penalty.lateBy !== 0;
      if ((isLate && penalty.amount !== latePenalty) || (!isLate && penalty.amount !== 0)) {
        particulars.push({
          userId,
          latePenalty: penalty.amount!,
        });
      }
    }

    await exportActivityDetails({
      activityId,
      activityType: 'assignment',
      latePenalty: latePenalty!,
      maxScore: maxScore!,
      particulars,
    });

    onExport();
  }, [activityId, individualPenaltyMap, latePenalty, maxScore, onExport]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant='large'
      classes={{ dialog: 'activity', title: 'activity__header' }}
      title={
        <>
          <div className='activity__number'>{`Assignment ${activity?.activityNum}`}</div>
          <div className='activity__title'>{activity?.title}</div>
          <div className='activity__total'>
            {toFixedIfFloat(activity?.details.avgScore || 0)}% average score
          </div>
        </>
      }
      footer={
        <Button
          disabled={isExporting}
          variant='outlined'
          color='info'
          size='large'
          onClick={exportActivityData}
        >
          {isExporting ? 'Exporting...' : 'Export Scores'}
        </Button>
      }
    >
      {isLoading || !activity ? (
        <div className='loader-wrapper'>
          <LoaderSpinner />
        </div>
      ) : (
        <>
          <div className='activity__details'>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>PUBLISHED BY</div>
              <div className='activity__detail-value'>{activity.publishedBy.name}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>
                <div className='tag tag--grey'>DUE ON</div>
              </div>
              <div className='activity__detail-value'>
                {activity.dueDateTime > 0 ? dt.format(activity.dueDateTime, DATE_FORMAT) : '--'}
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>SUBMISSIONS</div>
              <div className='activity__detail-value'>{activity.details.numSubmissions}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>LATE SUBMISSIONS</div>
              <div className='activity__detail-value'>{activity.details.lateSubmissions}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>LATE PENALTY</div>
              <div className='activity__detail-value'>
                <PercentInput
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  initValue={activity.details.latePenalty}
                  onchange={(value) => setLatePenalty(value)}
                  placeholder='Penalty'
                />
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>MAXIMUM SCORE</div>
              <div className='activity__detail-value'>
                <TextField
                  type='number'
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  value={maxScore}
                  onchange={(e) => setMaxScore(+e.target.value)}
                  placeholder='Maximum Score'
                />
              </div>
            </div>
          </div>
          <div className='activity__body'>
            <table className='activity__table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Late By</th>
                  <th>Score Attained</th>
                  <th className='activity__penalty-cell'>Late Submission Penalty</th>
                  <th>Final Score</th>
                </tr>
              </thead>
              <tbody>
                {activity.details.students.map((s) => (
                  <tr key={s.userId}>
                    <td>
                      <div className='activity__user'>{s.name}</div>
                    </td>
                    {!s.submitted ? (
                      <>
                        <td className='light-grey'>Unsubmitted</td>
                        <td className='light-grey'>0</td>
                        <td className='light-grey'>0</td>
                        <td className='light-grey'>0%</td>
                      </>
                    ) : !s.graded ? (
                      <>
                        <td className={getStylesForLateBy(s.lateBy)}>{getLateBy(s.lateBy)}</td>
                        <td className='bad'>Ungraded</td>
                        <td className='good'>0</td>
                        <td className='bad'>Ungraded</td>
                      </>
                    ) : (
                      <>
                        <td className={getStylesForLateBy(s.lateBy)}>{getLateBy(s.lateBy)}</td>
                        <td className={getStylesForScore(s.score)}>{toFixedIfFloat(s.score)}%</td>
                        <td
                          className={cn(
                            'activity__penalty-cell',
                            getStylesForPenalty(individualPenaltyMap[s.userId]?.amount || 0)
                          )}
                        >
                          <PercentInput
                            wrapperClassName='activity__score-input-wrapper'
                            selector='activity__score-input'
                            initValue={s.lateBy === 0 ? 0 : latePenalty}
                            onchange={(value) =>
                              setIndividualPenaltyMap({
                                type: 'UPDATE_PENALTY',
                                userId: s.userId,
                                amount: +(value || 0),
                              })
                            }
                            placeholder='Penalty'
                          />
                        </td>
                        <td
                          className={getStylesForScore(
                            s.score - (individualPenaltyMap[s.userId]?.amount || 0)
                          )}
                        >
                          {toFixedIfFloat(s.score - (individualPenaltyMap[s.userId]?.amount || 0))}%
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default AssignmentParticipations;
